import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '../../../lib/cn';
import { Spinner } from '../../core/Spinner';
import { Field, fieldDescribedBy } from './Field';
import { controlBoxClass } from './controlStyles';
import type { ControlSize } from './controlStyles';
import { useField } from './useField';

/* =============================================================================
   Input — free text.
   -----------------------------------------------------------------------------
   The control *is* the box: a real `<input>` carrying `controlBoxClass`, so the
   whole state ladder (idle / hover / focus / disabled / readOnly / error /
   success) comes from the shared skin and flips with the theme.

   It brings no type validation of its own — the consumer's `validate` is the
   only rule, and `useField` owns when that rule is revealed (first blur, or the
   form's first submit attempt).
   ============================================================================= */

export interface InputProps
  extends Omit<ComponentPropsWithRef<'input'>, 'value' | 'onChange' | 'id' | 'size' | 'required' | 'children'> {
  id: string;
  label?: ReactNode;
  description?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  /** Extra type validation, on top of the component's own. */
  validate?: (value: string) => string | null;
  /** A submit-level or server error. Wins over the component's own validation. */
  error?: string | null;
  /** Raises the inline-save "Saved!" state. */
  success?: boolean;
  successMessage?: ReactNode;
  size?: ControlSize;
  loading?: boolean;
  /** Bumped by the form on a submit attempt, to reveal errors. */
  submitCount?: number;
}

/** Room for the absolutely positioned spinner, so the text never runs under it. */
const LOADING_PAD: Record<ControlSize, string> = {
  sm: 'pr-8',
  md: 'pr-9',
  lg: 'pr-10',
};

export function Input({
  id,
  label,
  description,
  value,
  onChange,
  required = false,
  validate,
  error,
  success = false,
  successMessage,
  size = 'md',
  loading = false,
  submitCount,
  disabled,
  readOnly,
  onBlur,
  type = 'text',
  className,
  ref,
  ...rest
}: InputProps) {
  const field = useField({ value, onChange, validate, required, submitCount, success });
  const message = error ?? field.error;
  const invalid = Boolean(message);
  const savedMessage = field.success ? (successMessage ?? 'Saved!') : undefined;
  const describedBy = fieldDescribedBy(id, {
    description: Boolean(description),
    message: Boolean(message ?? savedMessage),
  });

  const control = (
    <input
      {...rest}
      ref={ref}
      type={type}
      id={id}
      value={value}
      onChange={(event) => onChange(event.currentTarget.value)}
      onBlur={(event) => {
        // `...rest` is spread first, so the consumer's own `onBlur` would
        // otherwise be silently replaced by this one.
        onBlur?.(event);
        field.onBlur();
      }}
      disabled={disabled}
      readOnly={readOnly}
      // Deliberate: the native `required` attribute is NOT set — the form is
      // `noValidate` and this library's own error UI owns validation; `aria-required` carries the semantics.
      aria-required={required || undefined}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-busy={loading || undefined}
      className={cn(
        controlBoxClass({ size, invalid, success: Boolean(savedMessage), disabled, readOnly }),
        loading && LOADING_PAD[size],
        className,
      )}
    />
  );

  return (
    <Field
      id={id}
      label={label}
      description={description}
      required={required}
      disabled={disabled}
      invalid={invalid}
      error={message}
      successMessage={savedMessage}
      size={size}
    >
      {loading ? (
        <div className="relative w-full min-w-0">
          {control}
          <Spinner
            size="sm"
            tone="muted"
            aria-hidden="true"
            className="absolute right-2.5 top-1/2 z-20 -translate-y-1/2"
          />
        </div>
      ) : (
        control
      )}
    </Field>
  );
}
